import { z } from 'zod';

import { discountSchema, itemDiscountSchema } from '@/shared/validation/form.validation';

export const userSchema = z.object({
  id: z.number().int().positive(),
  username: z.string(),
  email: z.email(),
  roles: z.array(z.string()).nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  company: z.string().nullable(),
  country: z.string().nullable(),
});

export const userInfoResultSchema = z.object({
  user: userSchema.nullable(),
});

export const pictureSchema = z.object({
  default: z.object({
    width: z.number(),
    height: z.number(),
    url: z.string(),
    orientation: z.enum(['landscape', 'portrait']),
  }),
  sources: z.array(
    z.object({
      format: z.string(),
      srcSet: z.array(
        z.object({
          media: z.string(),
          width: z.number(),
          height: z.number(),
          url: z.string(),
        }),
      ),
    }),
  ),
});
export type PictureSchema = z.infer<typeof pictureSchema>;

export const equipmentProductSchema = z.object({
  id: z.number(),
  ident: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
  picture: pictureSchema.nullable().optional(),
  price: z.number().nullable().optional(),
});
export type EquipmentProduct = z.infer<typeof equipmentProductSchema>;

export const equipmentItemTypeSchema = z.enum(['obligatory', 'additional']);
export type EquipmentItemType = z.infer<typeof equipmentItemTypeSchema>;

export const equipmentItemStatusSchema = z.enum(['', 'disabled', 'selected', 'error']);
export type EquipmentItemStatus = z.infer<typeof equipmentItemStatusSchema>;

export const equipmentItemSchema = z.object({
  id: z.number(),
  ident: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  categoryOrder: z.number().nullable().default(0),
  image: z.string().nullable().optional(),
  picture: pictureSchema.nullable().optional(),
  price: z.number().nullable().optional(),
  totalPrice: z.number().nullable().optional(),
  catalogDescription1: z.string().nullable(),
  catalogDescription2: z.string().nullable(),
  warning: z.string().nullable(),
  recommended: z.boolean(),
  status: equipmentItemStatusSchema,
  quantity: z
    .number()
    .nullable()
    .transform((v) => v ?? 1),
  unit: z.string().nullable().optional(),
  type: equipmentItemTypeSchema,
});
export type EquipmentItem = z.infer<typeof equipmentItemSchema>;

export const itemDiscountPropsSchema = z.object({
  discountValue: z.number(),
  priceWithDiscount: z.number().nullable(),
});
export type ItemDiscountProps = z.infer<typeof itemDiscountPropsSchema>;

export type EquipmentItemWithDiscount = EquipmentItem & ItemDiscountProps;

export const equipmentCategorySchema = z.object({
  id: z.number(),
  title: z.string().nullable().optional(),
  order: z.number(),
  mandatory: z.boolean(),
  items: z.array(equipmentItemSchema),
});
export type EquipmentCategory = z.infer<typeof equipmentCategorySchema>;

export const equipmentGroupSchema = z.object({
  id: z.number(),
  title: z.string().nullable().optional(),
  type: z.enum(['obligatory', 'additional']),
  categories: z.array(equipmentCategorySchema),
});
export type EquipmentGroup = z.infer<typeof equipmentGroupSchema>;

export const equipmentInfoResultSchema = z.object({
  product: equipmentProductSchema,
  groups: z.array(equipmentGroupSchema),
});
export type EquipmentInfoResult = z.infer<typeof equipmentInfoResultSchema>;

export const setSavedConfigResultSchema = z.string().min(1);

export const getSavedConfigResultSchema = z.object({
  product: z.number(),
  selected: z.string(),
  quoteSent: z.boolean().optional(),
  discounts: z.array(discountSchema),
  itemDiscounts: z.array(itemDiscountSchema),
});

export const sendEmailResultSchema = z.object({
  success: z.boolean(),
  message: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});

export const cartStatusSchema = z.enum(['pending', 'submitted']);
export type CartStatus = z.infer<typeof cartStatusSchema>;

export const cartItemProductSchema = z
  .object({
    id: z.number().nullable().optional(),
    title: z.string().nullable().optional(),
    image: z.string().nullable().optional(),
    picture: pictureSchema.nullable().optional(),
    ident: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    price: z.number().nullable().optional(),
  })
  .nullable();

export const cartItemPriceSchema = z
  .object({
    base: z.number(),
    equipment_discount: z.number(),
    after_equipment: z.number(),
    configuration_discount: z.number(),
    after_config: z.number(),
  })
  .nullable();

export const cartItemPartSchema = z.object({
  id: z.number(),
  title: z.string().nullable().optional(),
});

export const cartItemSchema = z.object({
  position: z.number().nullable(),
  configCode: z.string(),
  product: cartItemProductSchema,
  selectedParts: z.array(cartItemPartSchema),
  generalDiscounts: z.array(discountSchema),
  itemDiscounts: z.array(itemDiscountSchema),
  price: cartItemPriceSchema,
});

export const cartTotalsSchema = z.object({
  subtotal: z.number().nullable(),
  equipment_discounts_sum: z.number().nullable(),
  configuration_discounts_sum: z.number().nullable(),
  cart_discounts_sum: z.number().nullable(),
  total_pre_vat: z.number().nullable(),
  vat_percent: z.number(),
  vat: z.number().nullable(),
  total: z.number().nullable(),
  currency: z.string(),
});

export const cartResultSchema = z.object({
  status: cartStatusSchema,
  discount: z.number(),
  items: z.array(cartItemSchema),
  totals: cartTotalsSchema,
});

export const quoteRequestResultSchema = z.object({
  success: z.boolean(),
  message: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});
